// TODO: fix func names
/* eslint-disable func-names */
// TODO: Fix types and remove use of 'any'
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react'
import {
  axisBottom,
  axisLeft,
  bisector,
  extent,
  line,
  max,
  min,
  pointer,
  scaleLinear,
  scaleTime,
  select,
  timeFormat,
} from 'd3'
import { nest } from 'd3-collection'
import moment from 'moment'

import Spinner from './Spinner'
import {
  drawGraphCircle,
  drawVertLine,
  getFilterData,
  getIndexDetail,
  getLabel,
  getLineGraphColor,
  getMonthCount,
  getXAxisTickValues,
  getXAxisTimeFormat,
  getXScaleDomain,
  isSingleDate,
} from './util'
import { GROUP_TYPE } from '../constants/Filter'
import { GroupedIndex, Index } from '../store/StateObjects'

import '../styles/graph.css'

import 'd3-time-format'

export interface GraphProps {
  mobile: boolean
  indexData: Index[]
  indexDetailType: string
  groupBy: GROUP_TYPE
}

export const Graph: React.FC<GraphProps> = ({ mobile, indexData, indexDetailType, groupBy }) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const width = 1080
  const height = mobile ? 2070 : 450
  const transitionTime = 2000

  // generate d3 graph
  useEffect(() => {
    if (indexData.length === 0) {
      return
    }

    const margin = { top: 10, right: 20, bottom: 20, left: 220 }
    const xWidth = 1320 - margin.left - margin.right
    const xHeight = mobile ? 2100 : 450 - margin.top - margin.bottom
    const sortedData: Index[] = indexData.sort((a, b) => +a.week - +b.week)
    const xDomain = extent(sortedData, (d: Index) => d.week) as [Date, Date]
    const [startDate, endDate] = xDomain

    const monthCount = getMonthCount(startDate, endDate)
    const isSingleMonth = monthCount === 1
    const singleDate = isSingleDate(startDate, endDate)
    const xScaleDomain = getXScaleDomain(startDate, endDate)

    const xScale = scaleTime()
      .range([50, xWidth - 50])
      .domain(xScaleDomain)
      .nice()

    const yScale = scaleLinear()
      .range([xHeight, 0])
      .domain([
        min(sortedData, (d: Index) => getIndexDetail(indexDetailType, d).refi_index) - 30,
        max(sortedData, (d: Index) => getIndexDetail(indexDetailType, d).refi_index) + 30,
      ])

    const xAxis = axisBottom(xScale)
      .tickValues(getXAxisTickValues(startDate, endDate))
      .tickFormat(timeFormat(getXAxisTimeFormat(isSingleMonth, mobile)))

    const svg = select(svgRef.current)

    // clear graph before loading data
    // svg.selectAll(".line").remove();

    svg
      .select('.x-axis')
      .style('transform', `translateY(${xHeight}px)`)
      .transition()
      .duration(transitionTime)
      .call(xAxis)

    const yAxis = axisLeft(yScale).tickSize(-xWidth).tickPadding(16)

    svg.select('.y-axis').transition().duration(transitionTime).call(yAxis)

    svg
      .selectAll('.y-axis line')
      .filter((value: number) => value === 100)
      .style('stroke-width', '2px')
      .style('stroke', '#1F2142')

    svg.selectAll('.y-label').remove()

    svg
      .append('text')
      .attr('y', mobile ? -135 : -77)
      .attr('x', 0 - xHeight / 2)
      .attr('dy', '1em')
      .attr('class', 'y-label')
      .style('text-anchor', 'middle')
      .text('Index')

    // group by key for multi line graph
    const groupedIndex = nest<Index>()
      .key((d) => d[groupBy])
      .entries(sortedData)

    const lineNo = groupedIndex.length

    // clear last line, point and line before loading data
    if (lineNo > 1) {
      svg
        .selectAll(['.last-point', '.vertical-line', '.single-point'])
        .transition()
        .duration(transitionTime)
        .style('opacity', 0)
        .remove()
    } else if (lineNo === 1 || singleDate) {
      svg.selectAll(['.last-point', '.vertical-line', '.single-point']).remove()
    }

    svg.selectAll(['.listening-rect', '.dotted', '.tooltip-circle', '.svg-tooltip']).remove()

    svg.selectAll('.line').transition().duration(transitionTime).style('opacity', 0).remove()

    // generates the "d" attribute of a path element
    const myLine = () =>
      line()
        .x((d: Index) => xScale(d.week))
        .y((d: Index) => yScale(getIndexDetail(indexDetailType, d).refi_index))

    // renders path element, and attaches
    const u = svg.selectAll('.line').data(groupedIndex)

    // update the line chart with data
    // the "d" attribute from line generator above
    u.enter()
      .append('path')
      .attr('class', 'line')
      .merge(u)
      .transition()
      .duration(transitionTime)
      .attr('d', (d: GroupedIndex) => myLine()(d.values))
      .style('opacity', 1)
      .attr('fill', 'none')
      .attr('stroke', (d: GroupedIndex) => {
        return getLineGraphColor(lineNo > 1 ? d.key : '')
      })
      .attr('stroke-width', '5px')
      .on('end', (d: GroupedIndex) => {
        const { values } = d
        const lastData = values[values.length - 1]
        const { week } = lastData
        const index = getIndexDetail(indexDetailType, lastData).refi_index
        if (lineNo === 1 && svg.select('.vertical-line').empty()) {
          drawVertLine(svg, xScale(week), 'vertical-line')
        }
        if (lineNo === 1 || singleDate) {
          drawGraphCircle(
            svg,
            xScale(week),
            yScale(index),
            singleDate ? 'single-point' : 'last-point',
            lineNo,
            singleDate ? d.key : '',
          )
        }
      })

    // Hover mouse event
    const onMouseMove = (e: MouseEvent) => {
      const mousePosition = pointer(e)
      const hoveredXDate = xScale.invert(mousePosition[0])
      const hoveredYIndex = yScale.invert(mousePosition[1])
      let xDataPoint
      let hoverKey = ''
      let i = 0

      for (i = 0; i < groupedIndex.length; i++) {
        // Find the closest point by using bisector to get the insertion point ot index.
        const bisectLeft = bisector(function (d: any) {
          return d.week
        }).left
        const bisectRight = bisector(function (d: any) {
          return d.week
        }).right
        let closestXIndex =
          bisectLeft(groupedIndex[i].values, hoveredXDate) || bisectRight(groupedIndex[i].values, hoveredXDate)
        closestXIndex = closestXIndex === groupedIndex[i].values.length ? closestXIndex - 1 : closestXIndex
        const closestXDataPoint = groupedIndex[i].values[closestXIndex]

        // Get difference between hovered Y index and extracted closest data
        const indexDiff = hoveredYIndex.toFixed(4) - getIndexDetail(indexDetailType, closestXDataPoint).refi_index

        if (!singleDate) {
          if (indexDiff > -5.5 && indexDiff < 5.5) {
            xDataPoint = closestXDataPoint
            hoverKey = groupedIndex[i].key
            break
          }
        }

        // Get date anchors for single date entries
        const dateLeft = moment(closestXDataPoint.week).subtract(45, 'minutes').toDate()
        const dateRight = moment(closestXDataPoint.week).add(45, 'minutes').toDate()

        if (moment(hoveredXDate).isBetween(dateLeft, dateRight) && indexDiff > -3.5 && indexDiff < 3.5) {
          xDataPoint = closestXDataPoint
          hoverKey = groupedIndex[i].key
          break
        }
      }

      if (xDataPoint) {
        svg.selectAll(['.svg-tooltip', '.tooltip-circle', '.last-point', '.vertical-line']).remove()

        const refiDetail = getIndexDetail(indexDetailType, xDataPoint)
        const closestXValue = xDataPoint.week
        const closestYValue = refiDetail.refi_index
        const formatDate = timeFormat('%d %B %Y')
        const x = xScale(closestXValue)
        const y = yScale(closestYValue)

        const tooltip = svg
          .append('foreignObject')
          .attr('x', x > xWidth * 0.75 ? x - (mobile ? 446 : 246) : x + 12)
          .attr('y', () => {
            let yAdjust = lineNo > 1 ? 125 : 106
            if (mobile) {
              yAdjust = lineNo > 1 ? 227 : 206
            }
            return y - yAdjust
          })
          .attr('width', mobile ? '435px' : '235px')
          .attr('height', () => {
            if (mobile) {
              return lineNo > 1 ? '450px' : '412px'
            }
            return lineNo > 1 ? '250px' : '212px'
          })
          .attr('class', 'svg-tooltip')
          .append('xhtml:div')
          .attr('class', `tooltip${lineNo > 1 ? ' multi' : ''}${x > xWidth * 0.75 ? ' right' : ' left'}`)

        // Get Refi index detail tooltip display details
        const getSignedIndex = (n: number) => {
          if (!n) {
            return '0.0'
          }

          return (n <= 0 ? '' : '+') + n.toFixed(1)
        }

        const getIndexClass = (n: number) => {
          if (!n) {
            return ''
          }

          return n < 0 ? 'neg' : 'pos'
        }

        // Tooltip html
        tooltip.html(
          `<div class="reif-index-key"><b>${lineNo > 1 ? getLabel(hoverKey, getFilterData(groupBy)) : ''}</b></div>` +
            `<div class="refi-index-date">${formatDate(xDataPoint.week)}</div>` +
            `<div class="refi-index">Refi ${
              indexDetailType === 'VOLUME' ? 'Volume' : 'Value'
            } Index: <b>${closestYValue.toFixed(1)}</b><div>` +
            `<div class="refi-index-details">` +
            `<div>% change week: <span class="sign ${getIndexClass(refiDetail.change_week)}">${getSignedIndex(
              refiDetail.change_week,
            )}</span></div>` +
            `<div>% change month: <span class="sign ${getIndexClass(refiDetail.change_month)}">${getSignedIndex(
              refiDetail.change_month,
            )}</span></div>` +
            `<div>% change quarter: <span class="sign ${getIndexClass(refiDetail.change_quarter)}">${getSignedIndex(
              refiDetail.change_quarter,
            )}</span></div>` +
            `<div>% change year: <span class="sign ${getIndexClass(refiDetail.change_year)}">${getSignedIndex(
              refiDetail.change_year,
            )}</span></div></div>`,
        )

        if (svg.select('.vertical-line').empty()) {
          drawVertLine(svg, x, 'vertical-line')
        }

        // For singleDate entries, rewrite points so lines and point don;t overlap
        if (singleDate && lineNo > 1) {
          svg.selectAll('.single-point').remove()
          let z = 0
          for (z = 0; z < groupedIndex.length; z++) {
            const { values } = groupedIndex[z]
            const lastData = values[0]
            const { week } = lastData
            const index = getIndexDetail(indexDetailType, lastData).refi_index
            drawGraphCircle(svg, xScale(week), yScale(index), 'single-point', lineNo, groupedIndex[z].key, '', 0)
          }
        }
        drawGraphCircle(svg, x, y, 'tooltip-circle', lineNo, hoverKey, 'white', 2)
      } else {
        if (lineNo > 1 || !singleDate) {
          svg.select('.vertical-line').remove()
        }
        svg.selectAll(['.svg-tooltip', '.tooltip-circle', '.last-point']).remove()
        if (lineNo === 1 && !singleDate) {
          const { values } = groupedIndex[0]
          const lastData = values[values.length - 1]
          const { week } = lastData
          const index = getIndexDetail(indexDetailType, lastData).refi_index
          drawVertLine(svg, xScale(week), 'vertical-line')
          drawGraphCircle(svg, xScale(week), yScale(index), 'last-point', lineNo, '', '', 0)
        }
      }
    }

    // Create a canvasss to capture hover events
    svg
      .append('rect')
      .attr('class', 'listening-rect')
      .attr('width', xWidth)
      .attr('height', xHeight)
      .on('mousemove', onMouseMove)
  }, [mobile, indexData, indexDetailType, groupBy])

  return (
    <div className="refi-index-chart">
      {indexData.length === 0 && (
        <div className="spinner-container">
          <Spinner />
        </div>
      )}
      <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`}>
        <g className="x-axis" />
        <g className="y-axis" />
      </svg>
    </div>
  )
}
